/**
 * Thesis Fit — Pure Computation Functions
 *
 * All functions are pure (no React, no side effects).
 * Thesis Score = Sum(dimension_score * thesis_weight).
 */

import type { ScoringDimension } from '@/lib/scoring/methodology';
import { DIMENSION_ORDER, DIMENSION_LABELS } from '@/lib/scoring/methodology';
import type { ThesisScore, ThesisDimensionDetail, FitLabel } from './types';
import {
  WEIGHT_CONSTRAINTS,
  FIT_THRESHOLDS,
  FIT_COLORS,
  FIT_LABELS_DISPLAY,
} from './types';

// =============================================================================
// Core Computation
// =============================================================================

/**
 * Compute the Thesis Score by re-weighting dimension scores.
 *
 * @param dimensionScores - Map of dimension → score (0–100)
 * @param thesisWeights  - Map of dimension → weight (sum = 1.0)
 * @param inverioScore   - The overall Inverio Score for comparison
 */
export function computeThesisScore(
  dimensionScores: Record<string, number>,
  thesisWeights: Record<ScoringDimension, number>,
  inverioScore: number,
): ThesisScore {
  const neutralWeight = 1 / DIMENSION_ORDER.length;
  let thesisTotal = 0;

  const dimensionDetails: ThesisDimensionDetail[] = DIMENSION_ORDER.map((dim) => {
    const score = dimensionScores[dim] ?? 0;
    const weight = thesisWeights[dim] ?? neutralWeight;
    const contribution = score * weight;
    thesisTotal += contribution;

    return {
      dimension: dim,
      label: DIMENSION_LABELS[dim],
      score,
      thesisWeight: weight,
      neutralWeight,
      weightedContribution: contribution,
      weightDelta: weight - neutralWeight,
    };
  });

  const thesisScore = Math.round(thesisTotal);
  const fitGap = thesisScore - inverioScore;
  const fitLabel = getFitLabel(fitGap);

  return {
    thesisScore,
    inverioScore,
    fitGap,
    fitLabel,
    fitColor: FIT_COLORS[fitLabel],
    dimensionDetails,
    summary: generateSummary(thesisScore, inverioScore, fitGap, dimensionDetails),
  };
}

// =============================================================================
// Fit Label
// =============================================================================

export function getFitLabel(gap: number): FitLabel {
  for (const threshold of FIT_THRESHOLDS) {
    if (gap >= threshold.min) return threshold.label;
  }
  return 'mismatch';
}

// =============================================================================
// Weight Validation
// =============================================================================

export interface WeightValidation {
  valid: boolean;
  errors: string[];
}

export function validateWeights(
  weights: Record<ScoringDimension, number>,
): WeightValidation {
  const errors: string[] = [];
  const { min, max, sumTarget, tolerance } = WEIGHT_CONSTRAINTS;

  for (const dim of DIMENSION_ORDER) {
    const w = weights[dim];
    if (w === undefined) {
      errors.push(`Missing weight for ${DIMENSION_LABELS[dim]}`);
    } else if (w < min - tolerance) {
      errors.push(`${DIMENSION_LABELS[dim]} weight (${(w * 100).toFixed(1)}%) is below minimum ${(min * 100).toFixed(0)}%`);
    } else if (w > max + tolerance) {
      errors.push(`${DIMENSION_LABELS[dim]} weight (${(w * 100).toFixed(1)}%) exceeds maximum ${(max * 100).toFixed(0)}%`);
    }
  }

  const sum = Object.values(weights).reduce((s, v) => s + v, 0);
  if (Math.abs(sum - sumTarget) > tolerance) {
    errors.push(`Weights sum to ${(sum * 100).toFixed(1)}% instead of 100%`);
  }

  return { valid: errors.length === 0, errors };
}

// =============================================================================
// Guardrail Warnings (non-blocking)
// =============================================================================

export function getGuardrailWarnings(
  weights: Record<ScoringDimension, number>,
): string[] {
  const warnings: string[] = [];
  const { max, min } = WEIGHT_CONSTRAINTS;

  const atMax = DIMENSION_ORDER.filter((d) => weights[d] >= max - 0.005);
  const atMin = DIMENSION_ORDER.filter((d) => weights[d] <= min + 0.005);

  if (atMax.length >= 3) {
    warnings.push(
      `${atMax.length} dimensions at maximum weight may reduce differentiation`,
    );
  }
  if (atMin.length >= DIMENSION_ORDER.length / 2) {
    warnings.push(
      `${atMin.length} dimensions at minimum weight — consider a more balanced approach`,
    );
  }

  // Check if top-heavy: top 3 dimensions hold > 50%
  const sorted = [...DIMENSION_ORDER].sort((a, b) => (weights[b] ?? 0) - (weights[a] ?? 0));
  const top3Sum = sorted.slice(0, 3).reduce((s, d) => s + (weights[d] ?? 0), 0);
  if (top3Sum > 0.50) {
    warnings.push(
      `Top 3 dimensions account for ${(top3Sum * 100).toFixed(0)}% of total weight`,
    );
  }

  return warnings;
}

// =============================================================================
// Weight Redistribution
// =============================================================================

/**
 * When one slider changes, redistribute proportionally among other dimensions.
 * Clamps all values to [min, max] and normalizes to sum = 1.0.
 */
export function redistributeWeights(
  weights: Record<ScoringDimension, number>,
  changedDim: ScoringDimension,
  newValue: number,
): Record<ScoringDimension, number> {
  const { min, max } = WEIGHT_CONSTRAINTS;
  const clamped = Math.max(min, Math.min(max, newValue));

  const otherDims = DIMENSION_ORDER.filter((d) => d !== changedDim);
  const otherTotal = otherDims.reduce((s, d) => s + (weights[d] ?? 0), 0);
  const targetOtherTotal = 1.0 - clamped;

  const result = { ...weights, [changedDim]: clamped };

  if (otherTotal > 0) {
    // Scale proportionally
    const scale = targetOtherTotal / otherTotal;
    for (const dim of otherDims) {
      result[dim] = Math.max(min, Math.min(max, (weights[dim] ?? 0) * scale));
    }
  } else {
    // Fallback: equal distribution
    const equal = targetOtherTotal / otherDims.length;
    for (const dim of otherDims) {
      result[dim] = Math.max(min, Math.min(max, equal));
    }
  }

  // Normalize: after clamping, the sum may not be exactly 1.0
  const total = Object.values(result).reduce((s, v) => s + v, 0);
  if (Math.abs(total - 1.0) > 0.0001) {
    const correction = (1.0 - clamped) / otherDims.reduce((s, d) => s + result[d], 0);
    for (const dim of otherDims) {
      result[dim] = Math.max(min, Math.min(max, result[dim] * correction));
    }
    // Final hard normalize
    const finalTotal = DIMENSION_ORDER.reduce((s, d) => s + result[d], 0);
    for (const dim of DIMENSION_ORDER) {
      result[dim] = result[dim] / finalTotal;
    }
  }

  return result;
}

// =============================================================================
// Summary Generation
// =============================================================================

function generateSummary(
  thesisScore: number,
  inverioScore: number,
  fitGap: number,
  details: ThesisDimensionDetail[],
): string {
  const direction = fitGap >= 0 ? 'above' : 'below';
  const absGap = Math.abs(fitGap);

  // Find top overweighted dimension
  const sorted = [...details].sort((a, b) => b.weightDelta - a.weightDelta);
  const topDim = sorted[0];

  const fitLabel = FIT_LABELS_DISPLAY[getFitLabel(fitGap)];

  if (absGap === 0) {
    return `Thesis Score ${thesisScore} matches the Inverio Score exactly (${fitLabel}).`;
  }

  return `Thesis Score ${thesisScore} is ${absGap} points ${direction} the Inverio Score (${inverioScore}), driven primarily by your emphasis on ${topDim.label}. ${fitLabel}.`;
}
